import { Tokens } from '@/assets/tokens'
import { Button, Flex, Selector } from '@/components'
import { useRouting } from '@/hooks'
import { Alert, Api } from '@/modules'
import { Str } from '@/utils/strings'
import { Close, Info, Plus, SmilingFace, Star } from '@icon-park/react'
import {
  EditablePolarityValues,
  LinkMentionsToBrandsRequest,
  LinkMentionsToBrandsResponse,
  PolarityID,
  PolarityValue,
  SearchResponse,
} from '@shared/models'
import { useEffect, useMemo, useState } from 'react'
import { useMentionsContext } from '../../Mentions.context'
import {
  Container,
  EmptyStateText,
  KeyMessageDeleteButton,
  KeyMessageTag,
  KeyMessageText,
  ModalDescription,
  ModalTitle,
  Overlay,
  SectionLabel,
  SectionTitle,
  SpokespersonTag,
  ThemeFormContainer,
  ThemeGroupLabel,
  ThemeTag,
  ThemeTagDeleteButton,
  ThemeTagText,
  WarningBox,
  WarningText,
} from '../EditModal/EditModal.styles'

interface LinkBrandsModalProps {
  onClose: () => void
}

interface ThemeTagToAdd {
  themeInternalID: string
  themeName: string
  themeGroupInternalID: string
  themeGroupName: string
  tempId: string
}

export function LinkBrandsModal({ onClose }: LinkBrandsModalProps): JSX.Element {
  const { companyID } = useRouting()
  const {
    selectedMentionIds,
    polarities,
    roles,
    isSearchingRoles,
    searchRoles,
    clearSelection,
    applyFilters,
    mentions,
  } = useMentionsContext()

  const { sendAlert } = Alert.Hook()

  const { binder: targetBrandBinder, selectedOptions: selectedTargetBrandOptions } = Selector.MultiBinder()
  const targetBrandIDs = useMemo(
    () => selectedTargetBrandOptions.map((option) => String(option.value)),
    [selectedTargetBrandOptions],
  )
  const isSingleTargetBrand = targetBrandIDs.length === 1
  const hasCompetitorBrandSelected = selectedTargetBrandOptions.some((option) => option.text.endsWith(' (C)'))

  const [brandOptions, setBrandOptions] = useState<SearchResponse[]>([])
  const [searchBrandsAPI, isLoadingBrands] = Api.PromiseQuery<SearchResponse[]>('brands/search')

  useEffect(() => {
    if (!companyID) return
    const loadBrands = async () => {
      const [data] = await searchBrandsAPI({
        company: companyID,
        size: 10000,
        orderBy: 'name:ASC',
        includeCompetitors: true,
      })
      if (data) setBrandOptions(data)
    }
    loadBrands()
  }, [companyID])

  const selectedMentions = useMemo(() => {
    if (!mentions?.elements || selectedMentionIds.size === 0) return []

    const selectedCompositeIds = Array.from(selectedMentionIds)
    const found = selectedCompositeIds
      .map((compositeId) => {
        const [postId, brandId] = compositeId.split('_')
        return mentions.elements.find((m) => m.postID === postId && m.brandID.toString() === brandId)
      })
      .filter(Boolean) as typeof mentions.elements

    return found
  }, [mentions?.elements, selectedMentionIds])

  const distinctPostIDs = useMemo(() => Array.from(new Set(selectedMentions.map((m) => m.postID))), [selectedMentions])
  const isSinglePost = distinctPostIDs.length === 1
  const singleSourceMention = isSinglePost ? selectedMentions.find((m) => m.postID === distinctPostIDs[0]) : undefined

  // Todas as menções JÁ CARREGADAS pra essas postagens — não só as linhas que o
  // usuário marcou. Uma postagem pode já estar vinculada a uma marca que não foi
  // marcada no checkbox (ex: usuário só selecionou a linha da Marca A, mas a
  // postagem já existe pra Marca B também) — precisamos saber disso pra não
  // oferecer Marca B como opção de novo.
  const allKnownMentionsForSelectedPosts = useMemo(() => {
    if (!mentions?.elements || distinctPostIDs.length === 0) return []
    const postIDSet = new Set(distinctPostIDs)
    return mentions.elements.filter((m) => postIDSet.has(m.postID))
  }, [mentions?.elements, distinctPostIDs])

  // Marcas que TODAS as postagens selecionadas já possuem — vinculá-las de novo seria
  // um no-op garantido pra cada uma, então não faz sentido oferecer como opção.
  // Marcas que só ALGUMAS das postagens já têm continuam disponíveis (ainda fazem
  // sentido pras demais).
  const brandIDsLinkedToAllSelectedPosts = useMemo(() => {
    if (distinctPostIDs.length === 0) return new Set<string>()

    const brandIDsByPostID = new Map<string, Set<string>>()
    for (const mention of allKnownMentionsForSelectedPosts) {
      if (!brandIDsByPostID.has(mention.postID)) brandIDsByPostID.set(mention.postID, new Set())
      brandIDsByPostID.get(mention.postID)!.add(mention.brandID)
    }

    return distinctPostIDs.reduce<Set<string>>((intersection, postID, index) => {
      const brandIDsForPost = brandIDsByPostID.get(postID) ?? new Set<string>()
      if (index === 0) return brandIDsForPost
      return new Set([...intersection].filter((brandID) => brandIDsForPost.has(brandID)))
    }, new Set<string>())
  }, [allKnownMentionsForSelectedPosts, distinctPostIDs])

  const availableBrandOptions = useMemo(
    () => brandOptions.filter((item) => !brandIDsLinkedToAllSelectedPosts.has(String(item.value))),
    [brandOptions, brandIDsLinkedToAllSelectedPosts],
  )

  const defaultPolarityOption = useMemo(() => {
    if (!singleSourceMention) return undefined
    const match = polarities?.find((item) => item.value === singleSourceMention.polarity)
    if (!match) return undefined
    return { text: Str.from(match.text).initCap, value: PolarityID.get(match.value as PolarityValue) ?? 0 }
  }, [singleSourceMention, polarities])

  const defaultRoleOption = useMemo(() => {
    if (!singleSourceMention?.role) return undefined
    const match = roles?.find((item) => item.text?.toUpperCase() === singleSourceMention.role?.toUpperCase())
    if (!match) return undefined
    return { text: Str.from(match.text).initCap, value: match.value }
  }, [singleSourceMention, roles])

  const { binder: bindPolarity, selectedOption: polarity } = Selector.Binder(defaultPolarityOption)
  const { binder: bindRole, selectedOption: role } = Selector.Binder(defaultRoleOption)

  const [themeTagsToAdd, setThemeTagsToAdd] = useState<ThemeTagToAdd[]>([])
  const [allThemeGroups, setAllThemeGroups] = useState<SearchResponse[]>([])
  const [themesByGroup, setThemesByGroup] = useState<Map<string, SearchResponse[]>>(new Map())
  const [loadingThemesForGroup, setLoadingThemesForGroup] = useState<Map<string, boolean>>(new Map())

  const [keyMessagesToAdd, setKeyMessagesToAdd] = useState<string[]>([])
  const [availableKeyMessages, setAvailableKeyMessages] = useState<{ text: string; value: string }[]>([])

  const [spokespersonsToAdd, setSpokespersonsToAdd] = useState<string[]>([])
  const [availableSpokespersons, setAvailableSpokespersons] = useState<SearchResponse[]>([])

  const { binder: bindNewThemeGroup, selectedOption: newThemeGroup, reset: resetNewThemeGroup } = Selector.Binder()
  const { binder: bindNewTheme, selectedOption: newTheme, reset: resetNewTheme } = Selector.Binder()
  const { binder: bindKeyMessage, selectedOption: selectedKeyMessage, reset: resetKeyMessage } = Selector.Binder()
  const { binder: bindSpokesperson, selectedOption: selectedSpokesperson, reset: resetSpokesperson } = Selector.Binder()

  const [searchThemeGroupsAPI, isLoadingThemeGroups] = Api.PromiseQuery<SearchResponse[]>('theme-groups/search')
  const [searchThemesAPI] = Api.PromiseQuery<SearchResponse[]>('themes/search')
  const [fetchKeyMessages, isLoadingKeyMessages] = Api.PromiseQuery<any>('key-messages')
  const [fetchSpokespersons, isLoadingSpokespersons] = Api.PromiseQuery<SearchResponse[]>('spokespersons/search')

  const { isRunning: isLinking, run: linkMentionsToBrands } = Api.Command<LinkMentionsToBrandsResponse>(
    Api.Methods.POST,
    'companies/:companyID/mentions/link-brands',
  )

  useEffect(() => {
    setThemeTagsToAdd([])
    setKeyMessagesToAdd([])
    setSpokespersonsToAdd([])
    resetNewThemeGroup()
    resetNewTheme()
    resetKeyMessage()
    resetSpokesperson()
    setThemesByGroup(new Map())

    if (!isSingleTargetBrand || !companyID) return

    const targetBrandID = targetBrandIDs[0]

    const loadTargetBrandCatalogs = async () => {
      const [themeGroupsData] = await searchThemeGroupsAPI({
        company: companyID,
        size: 10000,
        orderBy: 'name:ASC',
        brandIDs: [targetBrandID],
      })
      if (themeGroupsData) setAllThemeGroups(themeGroupsData)

      const [keyMessagesData] = await fetchKeyMessages({
        company: companyID,
        size: 10000,
        orderBy: 'message:ASC',
        brandIDs: [targetBrandID],
      })
      const messages = keyMessagesData?.elements ?? []
      const activeMessages = messages.filter((msg: any) => msg.isActive)
      setAvailableKeyMessages(activeMessages.map((msg: any) => ({ text: msg.message, value: msg.internalID })))

      const [spokespersonsData] = await fetchSpokespersons({
        company: companyID,
        size: 10000,
        brandIDs: [targetBrandID],
      })
      if (spokespersonsData) setAvailableSpokespersons(spokespersonsData)
    }

    loadTargetBrandCatalogs()
  }, [isSingleTargetBrand, targetBrandIDs.join(','), companyID])

  const loadThemesForGroup = async (themeGroupId: string) => {
    if (themesByGroup.has(themeGroupId) || !isSingleTargetBrand || !companyID) return

    setLoadingThemesForGroup((prev) => new Map(prev).set(themeGroupId, true))
    try {
      const [data] = await searchThemesAPI({
        company: companyID,
        marca: targetBrandIDs[0],
        grupoTematico: themeGroupId,
        size: 10000,
        fathersOnly: true,
        orderBy: 'value:ASC',
      })
      if (data) setThemesByGroup((prev) => new Map(prev).set(themeGroupId, data))
    } catch {
      sendAlert({
        type: 'error',
        title: 'Erro ao carregar temas',
        message: 'Não foi possível carregar os temas para o grupo selecionado.',
      })
    } finally {
      setLoadingThemesForGroup((prev) => new Map(prev).set(themeGroupId, false))
    }
  }

  useEffect(() => {
    if (newThemeGroup && isSingleTargetBrand) {
      loadThemesForGroup(String(newThemeGroup.value))
      resetNewTheme()
    }
  }, [newThemeGroup])

  const availableThemesForNewGroup = useMemo(() => {
    if (!newThemeGroup) return []
    const allThemes = themesByGroup.get(String(newThemeGroup.value)) || []
    const addedThemeIds = themeTagsToAdd.map((tag) => tag.themeInternalID)
    return allThemes.filter((theme) => !addedThemeIds.includes(String(theme.value)))
  }, [newThemeGroup, themesByGroup, themeTagsToAdd])

  const handleAddTheme = () => {
    if (!newThemeGroup || !newTheme) {
      sendAlert({
        type: 'error',
        title: 'Seleção incompleta',
        message: 'Selecione um grupo temático e um tema antes de adicionar.',
      })
      return
    }

    const themeName = availableThemesForNewGroup.find((t) => t.value === String(newTheme.value))?.name || newTheme.text

    setThemeTagsToAdd((prev) => [
      ...prev,
      {
        themeInternalID: String(newTheme.value),
        themeName,
        themeGroupInternalID: String(newThemeGroup.value),
        themeGroupName: newThemeGroup.text,
        tempId: `temp_${Date.now()}_${Math.random()}`,
      },
    ])
    resetNewThemeGroup()
    resetNewTheme()
  }

  const handleRemoveTheme = (tempId: string) => {
    setThemeTagsToAdd((prev) => prev.filter((tag) => tag.tempId !== tempId))
  }

  const handleAddKeyMessage = () => {
    if (!selectedKeyMessage) {
      sendAlert({
        type: 'error',
        title: 'Seleção incompleta',
        message: 'Selecione uma mensagem-chave antes de adicionar.',
      })
      return
    }
    const internalID = String(selectedKeyMessage.value)
    if (keyMessagesToAdd.includes(internalID)) {
      sendAlert({ type: 'error', title: 'Mensagem duplicada', message: 'Esta mensagem-chave já foi adicionada.' })
      return
    }
    setKeyMessagesToAdd((prev) => [...prev, internalID])
    resetKeyMessage()
  }

  const handleRemoveKeyMessage = (internalID: string) => {
    setKeyMessagesToAdd((prev) => prev.filter((id) => id !== internalID))
  }

  const handleAddSpokesperson = () => {
    if (!selectedSpokesperson) {
      sendAlert({ type: 'error', title: 'Seleção incompleta', message: 'Selecione um porta-voz antes de adicionar.' })
      return
    }
    const internalID = String(selectedSpokesperson.value)
    if (spokespersonsToAdd.includes(internalID)) {
      sendAlert({ type: 'error', title: 'Porta-voz duplicado', message: 'Este porta-voz já foi adicionado.' })
      return
    }
    setSpokespersonsToAdd((prev) => [...prev, internalID])
    resetSpokesperson()
  }

  const handleRemoveSpokesperson = (internalID: string) => {
    setSpokespersonsToAdd((prev) => prev.filter((id) => id !== internalID))
  }

  const handleSubmit = () => {
    if (targetBrandIDs.length === 0) {
      sendAlert({
        type: 'error',
        title: 'Selecione ao menos uma marca',
        message: 'Escolha para quais marcas as postagens serão vinculadas.',
      })
      return
    }

    const classification: LinkMentionsToBrandsRequest['classification'] = {
      ...(isSinglePost && polarity && { polarityInternalID: polarity.value as number }),
      ...(isSinglePost && role && { roleInternalID: role.value as number }),
      ...(isSingleTargetBrand &&
        themeTagsToAdd.length > 0 && {
          themeSelections: themeTagsToAdd.map((tag) => ({
            themeGroupInternalID: tag.themeGroupInternalID,
            themeInternalID: tag.themeInternalID,
          })),
        }),
      ...(isSingleTargetBrand &&
        keyMessagesToAdd.length > 0 && {
          keyMessageSelections: keyMessagesToAdd.map((keyMessageInternalID) => ({ keyMessageInternalID })),
        }),
      ...(isSingleTargetBrand &&
        spokespersonsToAdd.length > 0 && {
          spokespersonSelections: spokespersonsToAdd.map((spokespersonInternalID) => ({ spokespersonInternalID })),
        }),
    }

    linkMentionsToBrands({
      input: {
        mentionIds: Array.from(selectedMentionIds),
        targetBrandIDs,
        classification,
      },
      binds: [['companyID', companyID!]],
      onSuccess: (data: LinkMentionsToBrandsResponse) => {
        const skippedText =
          data.skippedAlreadyLinkedCount ? `, ${data.skippedAlreadyLinkedCount} já existiam e foram ignorados` : ''
        sendAlert({
          type: 'success',
          title: 'Marcas vinculadas',
          message: `${data.linkedCount} vínculo(s) criado(s) com sucesso${skippedText}.`,
        })
        clearSelection()
        applyFilters()
        onClose()
      },
      onFail: (error) => {
        sendAlert({
          type: 'error',
          title: 'Erro ao vincular marcas',
          message: `Não foi possível concluir a operação: ${error.message}`,
        })
      },
    })
  }

  return (
    <Overlay>
      <Container style={{ width: '60rem' }}>
        <Flex $type="row" $justify="center" $align="center" $fit style={{ position: 'relative' }}>
          <ModalTitle>Adicionar a Outras Marcas</ModalTitle>
          <Button.Root $variant="link" onClick={onClose} style={{ position: 'absolute', right: 0 }}>
            <Button.Icon value={Close} color={Tokens.ColorTextPrimary} />
          </Button.Root>
        </Flex>

        <ModalDescription>
          {distinctPostIDs.length} postagem(ns) selecionada(s). Escolha as marcas que ainda não possuem essas postagens.
        </ModalDescription>

        <Selector.Multi
          binder={targetBrandBinder}
          placeholder="Marcas-alvo"
          title="Marcas-alvo"
          options={availableBrandOptions.map((item) => ({ text: item.text, value: item.value }))}
          loading={isLoadingBrands}
          renderOptionsInPortal
          $fit={true}>
          <Selector.Icon value={Star} color={Tokens.ColorUIPrimary} />
        </Selector.Multi>

        {hasCompetitorBrandSelected && (
          <WarningBox>
            <Button.Icon value={Info} color="#856404" />
            <WarningText>
              Pelo menos uma marca selecionada é de uma empresa concorrente — vincular vai criar uma cópia dessa
              postagem lá também (canal, publicador e conteúdo), pra ela poder aparecer na listagem da empresa
              concorrente.
            </WarningText>
          </WarningBox>
        )}

        {isSinglePost ?
          <Flex $type="row" $justify="flex-start" $align="center" $fit $gap={16}>
            <Selector.Default
              {...bindPolarity}
              placeholder="Polaridade"
              options={
                polarities
                  ?.filter((item) => EditablePolarityValues.includes(item.value as PolarityValue))
                  .map((item) => ({
                    text: Str.from(item.text).initCap,
                    value: PolarityID.get(item.value as PolarityValue) ?? 0,
                  })) ?? []
              }>
              <Selector.Icon value={SmilingFace} color={Tokens.ColorUIPrimary} />
            </Selector.Default>

            <Selector.Searchable
              {...bindRole}
              placeholder="Papel"
              options={roles?.map((item) => ({ text: Str.from(item.text).initCap, value: item.value })) ?? []}
              onSearch={(searchParam: string): void => searchRoles({ name: searchParam })}
              loading={isSearchingRoles}>
              <Selector.Icon value={Star} color={Tokens.ColorUIPrimary} />
            </Selector.Searchable>
          </Flex>
        : <EmptyStateText>
            Como são postagens diferentes, cada uma pode já ter sua própria polaridade e papel — por isso esses campos
            não aparecem aqui: cada vínculo novo mantém o que a postagem correspondente já tem hoje.
          </EmptyStateText>
        }

        {!isSingleTargetBrand && (
          <EmptyStateText>
            Tema, mensagem-chave e porta-voz só podem ser definidos aqui quando exatamente 1 marca-alvo é selecionada.
            Com mais de uma marca, complete esses campos depois, marca a marca, pela edição normal.
          </EmptyStateText>
        )}

        {isSingleTargetBrand && (
          <>
            <Flex $type="column" $justify="flex-start" $align="stretch" $fit $gap={12}>
              <SectionTitle>Porta-voz</SectionTitle>
              <Flex $type="row" $justify="flex-start" $align="center" $fit $gap={12}>
                <Flex $type="column" $fit style={{ flex: 1, maxWidth: '400px' }}>
                  <Selector.Default
                    {...bindSpokesperson}
                    options={availableSpokespersons.map((item) => ({ text: item.text, value: item.value }))}
                    placeholder="Selecione um porta-voz"
                    loading={isLoadingSpokespersons}
                  />
                </Flex>
                <Button.Root $variant="secondary" onClick={handleAddSpokesperson}>
                  <Button.Icon value={Plus} />
                  <Button.Text value="Adicionar" />
                </Button.Root>
              </Flex>

              {spokespersonsToAdd.length > 0 && (
                <Flex $type="row" $justify="flex-start" $align="center" $fit $gap={8} style={{ flexWrap: 'wrap' }}>
                  {spokespersonsToAdd.map((internalID) => {
                    const item = availableSpokespersons.find((sp) => sp.value === internalID)
                    return (
                      <SpokespersonTag key={internalID}>
                        <KeyMessageText title={item?.text}>👤 {item?.text}</KeyMessageText>
                        <KeyMessageDeleteButton
                          onClick={() => handleRemoveSpokesperson(internalID)}
                          title="Remover porta-voz">
                          ×
                        </KeyMessageDeleteButton>
                      </SpokespersonTag>
                    )
                  })}
                </Flex>
              )}

              {spokespersonsToAdd.length === 0 && <EmptyStateText>Nenhum porta-voz selecionado.</EmptyStateText>}
            </Flex>

            <Flex $type="column" $justify="flex-start" $align="stretch" $fit $gap={12}>
              <SectionTitle>Mensagens-Chave</SectionTitle>
              <Flex $type="row" $justify="flex-start" $align="center" $fit $gap={12}>
                <Flex $type="column" $fit style={{ flex: 1, maxWidth: '400px' }}>
                  <Selector.Default
                    {...bindKeyMessage}
                    options={availableKeyMessages}
                    placeholder="Selecione uma mensagem-chave"
                    loading={isLoadingKeyMessages}
                  />
                </Flex>
                <Button.Root $variant="secondary" onClick={handleAddKeyMessage}>
                  <Button.Icon value={Plus} />
                  <Button.Text value="Adicionar" />
                </Button.Root>
              </Flex>

              {keyMessagesToAdd.length > 0 && (
                <Flex $type="row" $justify="flex-start" $align="center" $fit $gap={8} style={{ flexWrap: 'wrap' }}>
                  {keyMessagesToAdd.map((internalID) => {
                    const message = availableKeyMessages.find((msg) => msg.value === internalID)
                    return (
                      <KeyMessageTag key={internalID}>
                        <KeyMessageText title={message?.text}>
                          {message?.text || `Mensagem ${internalID}`}
                        </KeyMessageText>
                        <KeyMessageDeleteButton
                          onClick={() => handleRemoveKeyMessage(internalID)}
                          title="Remover mensagem-chave">
                          ×
                        </KeyMessageDeleteButton>
                      </KeyMessageTag>
                    )
                  })}
                </Flex>
              )}

              {keyMessagesToAdd.length === 0 && <EmptyStateText>Nenhuma mensagem-chave selecionada.</EmptyStateText>}
            </Flex>

            <Flex $type="column" $justify="flex-start" $align="stretch" $fit $gap={12}>
              <SectionTitle>Temas</SectionTitle>
              <ThemeFormContainer>
                <SectionLabel>Adicionar Tema</SectionLabel>
                <Flex $type="row" $justify="flex-start" $align="center" $fit $gap={12}>
                  <Flex $type="column" $fit style={{ flex: 1 }}>
                    <Selector.Default
                      {...bindNewThemeGroup}
                      placeholder={
                        allThemeGroups.length === 0 ? 'Nenhum grupo disponível' : 'Selecionar Grupo Temático'
                      }
                      options={allThemeGroups.map((group) => ({
                        text: Str.from(group.text).initCap,
                        value: group.value,
                      }))}
                      loading={isLoadingThemeGroups}
                    />
                  </Flex>
                  <Flex $type="column" $fit style={{ flex: 1 }}>
                    <Selector.Default
                      {...bindNewTheme}
                      placeholder={
                        !newThemeGroup ? 'Selecione um grupo primeiro'
                        : availableThemesForNewGroup.length === 0 ?
                          'Nenhum tema disponível'
                        : 'Selecionar Tema'
                      }
                      options={availableThemesForNewGroup.map((theme) => ({
                        text: Str.from(theme.text).initCap,
                        value: theme.value,
                      }))}
                      loading={loadingThemesForGroup.get(String(newThemeGroup?.value)) || false}
                    />
                  </Flex>
                  <Button.Root $variant="secondary" onClick={handleAddTheme}>
                    <Button.Icon value={Plus} color={Tokens.ColorTextSecondary} />
                    <Button.Text value="Adicionar" />
                  </Button.Root>
                </Flex>
              </ThemeFormContainer>

              {themeTagsToAdd.length > 0 && (
                <Flex $type="column" $justify="flex-start" $align="stretch" $fit $gap={12}>
                  {Object.entries(
                    themeTagsToAdd.reduce(
                      (acc, tag) => {
                        if (!acc[tag.themeGroupInternalID]) {
                          acc[tag.themeGroupInternalID] = { groupName: tag.themeGroupName, themes: [] }
                        }
                        acc[tag.themeGroupInternalID].themes.push(tag)
                        return acc
                      },
                      {} as Record<string, { groupName: string; themes: ThemeTagToAdd[] }>,
                    ),
                  ).map(([groupId, groupData]) => (
                    <Flex key={groupId} $type="column" $align="flex-start" $gap={6} $fit>
                      <ThemeGroupLabel>{groupData.groupName}:</ThemeGroupLabel>
                      <Flex $type="row" $align="center" $gap={8} style={{ flexWrap: 'wrap' }}>
                        {groupData.themes.map((tag) => (
                          <ThemeTag key={tag.tempId}>
                            <ThemeTagText title={tag.themeName}>{tag.themeName}</ThemeTagText>
                            <ThemeTagDeleteButton onClick={() => handleRemoveTheme(tag.tempId)} title="Remover tema">
                              ×
                            </ThemeTagDeleteButton>
                          </ThemeTag>
                        ))}
                      </Flex>
                    </Flex>
                  ))}
                </Flex>
              )}

              {themeTagsToAdd.length === 0 && <EmptyStateText>Nenhum tema selecionado.</EmptyStateText>}
            </Flex>
          </>
        )}

        <Flex $type="row" $justify="flex-end" $align="center" $fit $gap={16}>
          <Button.Root $variant="secondary" onClick={onClose} style={{ width: '7rem' }}>
            <Button.Text value="Cancelar" />
          </Button.Root>
          <Button.Root $variant="primary" onClick={handleSubmit} $loading={isLinking} style={{ width: '10rem' }}>
            <Button.Text value="Vincular" />
          </Button.Root>
        </Flex>
      </Container>
    </Overlay>
  )
}
